import { learningPathApiService, type LearningPathDto, type LearningPathFilterDto } from '@/services/api/learning-path-api.service';
import { reviewService } from '@/services/review.service';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  ChevronDown,
  Edit,
  Grid,
  List,
  Route,
  Search,
  SlidersHorizontal,
  Sparkle,
  Star,
  Target,
  BookOpen
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_CATALOG_PERMISSION, LEARNING_PATH_CREATION_PERMISSION } from '@/constants/constant-permissions';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { useDebounce } from '@/utils/debounce';

const LearningPathCard = ({ learningPath, isListView = false, onRatingClick, canWrite = true }: { learningPath: LearningPathDto, isListView?: boolean, onRatingClick?: (id: string) => void, canWrite?: boolean }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'ARCHIVED':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const thumbnailUrl = learningPath.thumbnail_image_url || learningPath.banner_image_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400';

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return '🟢 Published';
      case 'DRAFT':
        return '⚫ Draft';
      case 'ARCHIVED':
        return '🟠 Unpublished';
      default:
        return status;
    }
  };

  return (
    <div className={`card card-hover ${isListView ? 'flex flex-col sm:flex-row gap-3 sm:gap-4' : 'flex flex-col'}`}>
      <div className={`relative ${isListView ? 'w-full sm:w-48 flex-shrink-0' : ''}`}>
        <img
          src={thumbnailUrl}
          alt={learningPath.title}
          className={`w-full ${isListView ? 'h-40 sm:h-32' : 'h-40 sm:h-48'} object-cover rounded-lg`}
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(learningPath.status)} backdrop-blur-sm`}>
            {getStatusLabel(learningPath.status)}
          </span>
        </div>
      </div>

      <div className={`${isListView ? 'flex-1' : 'mt-3 sm:mt-4'} flex flex-col flex-1`}>
        <h3 className={`font-semibold text-gray-900 ${isListView ? 'text-base sm:text-lg' : 'text-sm sm:text-base'} line-clamp-2 mb-2`}>
          <Link
            to={canWrite ? `/admin/learning-paths/${learningPath._id}` : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            title={!canWrite ? 'You do not have permission to perform this action' : undefined}
            className={`hover:text-primary-600 ${!canWrite ? 'cursor-not-allowed opacity-70 pointer-events-auto' : ''}`}
          >
            {learningPath.title}
          </Link>
        </h3>
        <p className={`text-gray-600 text-xs sm:text-sm mb-3 ${isListView ? 'line-clamp-2' : 'line-clamp-3'}`}>
          {learningPath.description}
        </p>

        {/* Tags */}
        {learningPath.tags && learningPath.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {learningPath.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                {tag}
              </span>
            ))}
            {learningPath.tags.length > 3 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                +{learningPath.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          {/* Rating Display - Always show */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (canWrite && onRatingClick) {
                onRatingClick(learningPath._id);
              }
            }}
            disabled={!canWrite}
            className={`flex items-center gap-1 hover:bg-yellow-50 px-1.5 py-0.5 rounded transition-colors ${!canWrite ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            title={!canWrite ? 'You do not have permission to perform this action' : 'View reviews'}
          >
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{learningPath.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-gray-400">• {learningPath.review_count || 0} ratings</span>
          </button>
          <div className="flex items-center gap-1" title="Target Role">
            <Target className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{learningPath.target_role_or_skill}</span>
          </div>
          <div className="flex items-center gap-1" title="Total Courses">
            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{learningPath.total_courses} courses</span>
          </div>
          <div className="flex items-center gap-1" title="Enrolled">
            <Award className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{learningPath.total_enrolled} enrolled</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mt-auto">
          <div className="text-xs text-gray-500">
            {learningPath.difficulty_progression}
          </div>
          <Link
            to={canWrite ? `/admin/learning-paths/${learningPath._id}` : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            className={`btn btn-primary btn-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto ${
              !canWrite ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={!canWrite ? 'You do not have permission to manage learning paths' : undefined}
          >
            <Edit size={14} />
            Manage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function LearningPaths() {
  usePermissionRouteAccess([
    LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_READ,
    LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE,
  ]);
  const canWrite = useHasPermission([LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE]);
  const canCreateLP = useHasPermission([LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_WRITE]);

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(21);
  const [filters, setFilters] = useState({
    search: '',
    status: '' as '' | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED',
  });

  const debouncedSearch = useDebounce(filters.search, 1000);

  // Handle rating click - navigate to preview with reviews tab
  const handleRatingClick = (learningPathId: string) => {
    navigate(`/admin/learning-paths/preview/${learningPathId}?tab=reviews`);
  };

  const queryParams = useMemo((): LearningPathFilterDto => {
    const params: LearningPathFilterDto = {
      skip: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (filters.status) {
      params.status = filters.status;
    }

    return params;
  }, [filters, debouncedSearch, page, itemsPerPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-paths', queryParams],
    queryFn: () => learningPathApiService.listLearningPaths(queryParams),
    placeholderData: (prev) => prev,
  });

  const { data: metricsData } = useQuery({
    queryKey: ['learning-paths-metrics'],
    queryFn: () => learningPathApiService.getMetrics(),
    staleTime: 30000, // Cache for 30 seconds
  });

  if (error) toast.error('Failed to fetch learning paths.');

  const rawLearningPaths = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  // Fetch review stats for all learning paths (single API call)
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['learning-path-review-stats-bulk'],
    queryFn: () => reviewService.getBatchLearningPathStats(),
  });

  // Enrich learning paths with review stats
  const learningPaths = rawLearningPaths.map((learningPath) => {
    const stats = reviewStatsMap?.get(learningPath._id);
    return {
      ...learningPath,
      rating: stats?.avg_rating ?? learningPath.rating ?? 0,
      review_count: stats?.total_reviews ?? learningPath.review_count ?? 0,
    };
  });

  // Use metrics data from API
  const metrics = metricsData || {

  total_active_courses: 0,
    published: 0,
    drafts: 0,
    archived: 0,
    total_enrolled: 0,
    total_completed: 0,
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Draft and Published only — publishing is one-way, so there is no Unpublished
  // state to filter by.
  const statusOptions: SelectOption[] = [
    { value: '', label: '⚪ All Status' },
    { value: 'PUBLISHED', label: '🟢 Published' },
    { value: 'DRAFT', label: '⚫ Draft' },
  ];

  // SimpleSelect reports the '' ("All Status") option as null, so coerce it back.
  const handleStatusChange = (value: string | number | null) => {
    setPage(1);
    setFilters(prev => ({
      ...prev,
      status: (value == null ? '' : String(value)) as '' | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED',
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <SkeletonLoader type="custom" width="40px" height="40px" className="rounded-lg bg-white/20" />
              <div className="flex-1">
                <SkeletonLoader type="title" className="h-6 sm:h-8 w-40 sm:w-48 mb-2 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 sm:h-4 w-64 sm:w-96 bg-white/20 hidden sm:block" />
              </div>
            </div>
            <SkeletonLoader type="custom" width="180px" height="40px" className="rounded-lg bg-white/20" />
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <SkeletonLoader type="text" className="h-5 sm:h-7 w-12 sm:w-16 mb-1 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 w-16 sm:w-20 bg-white/20" />
              </div>
            ))}
          </div>
        </div>

        {/* Search and Controls Skeleton */}
        <div className="card p-4 md:p-6">
          <div className="flex flex-col gap-4 mb-6">
            <SkeletonLoader type="custom" height="44px" className="rounded-lg w-full" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <SkeletonLoader type="custom" width="140px" height="40px" className="rounded-lg" />
              <div className="flex items-center gap-2 sm:gap-3">
                <SkeletonLoader type="custom" width="80px" height="40px" className="rounded-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {Array.from({ length: 9 }).map((_, index) => (
            <div key={index} className="card flex flex-col">
              <SkeletonLoader type="custom" height="160px" className="rounded-lg mb-3 sm:mb-4" />
              <SkeletonLoader type="title" className="mb-2" />
              <SkeletonLoader type="text" className="mb-1" />
              <SkeletonLoader type="text" className="mb-3" />
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 sm:mb-4">
                <SkeletonLoader type="text" className="w-16" />
                <SkeletonLoader type="text" className="w-16" />
                <SkeletonLoader type="text" className="w-16" />
              </div>
              <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <SkeletonLoader type="text" className="w-24" />
                <SkeletonLoader type="custom" width="100px" height="32px" className="rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Route className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Learning Paths</h1>
              <p className="text-sm sm:text-base text-purple-100 hidden sm:block">AI-generated personalized learning journeys</p>
            </div>
          </div>
          <button
            onClick={() => canCreateLP && navigate('/admin/learning-paths/create')}
            disabled={!canCreateLP}
            className={`flex items-center justify-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors whitespace-nowrap ${
              canCreateLP ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'
            }`}
            title={!canCreateLP ? 'You do not have permission to create learning paths' : undefined}
          >
            <Sparkle className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-medium text-sm sm:text-base">Create Learning Path</span>
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{metrics.total_active_courses}</div>
            <div className="text-xs sm:text-sm text-purple-100">Total</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{metrics.published}</div>
            <div className="text-xs sm:text-sm text-purple-100">Published</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{metrics.drafts}</div>
            <div className="text-xs sm:text-sm text-purple-100">Drafts</div>
          </div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="card p-4 md:p-6">
        <div className="flex flex-col gap-4 mb-6">
          {/* Search bar - full width on mobile */}
          <div className="w-full">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search learning paths..."
                className="w-full pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm md:text-base"
              />
            </div>
          </div>

          {/* Controls row - stacked on mobile, side by side on tablet+ */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm md:text-base"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-600'}`}
                  aria-label="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-600'}`}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg mb-4 sm:mb-6">
            <SimpleSelect
              label="Status"
              value={filters.status}
              onChange={handleStatusChange}
              options={statusOptions}
              isSearchable={false}
            />
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    status: '',
                  });
                  setPage(1);
                }}
                className="w-full btn btn-secondary text-sm md:text-base"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Learning Paths Grid/List */}
      {learningPaths.length === 0 ? (
        <div className="text-center py-12 card">
          <Route className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No learning paths found</h3>
          <p className="text-gray-600 mb-4">
            {filters.search || filters.status
              ? 'Try adjusting your filters or create a new learning path.'
              : 'Create your first AI-generated learning path to get started.'}
          </p>
          <button
            onClick={() => canCreateLP && navigate('/admin/learning-paths/create')}
            disabled={!canCreateLP}
            className={`btn btn-primary inline-flex items-center gap-2 ${
              !canCreateLP ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={!canCreateLP ? 'You do not have permission to create learning paths' : undefined}
          >
            <Sparkle size={18} />
            Create Learning Path
          </button>
        </div>
      ) : (
        <>
          <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}`}>
            {learningPaths.map(learningPath => (
              <LearningPathCard
                key={learningPath._id}
                learningPath={learningPath}
                isListView={viewMode === 'list'}
                onRatingClick={handleRatingClick}
                canWrite={canWrite}
              />
            ))}
          </div>

          {/* Pagination - Always show when there's data */}
          {total > 0 && (
            <div className="mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[9, 18, 21, 30]}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
